import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IMAGES, ARTICLE_IMAGES } from '@/data/images';
import { posts, type LocalizedText } from '@/data/posts';

export default function ArticlesSection() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang ?? 'en';
  const pick = (lt: LocalizedText) => lt[currentLang as keyof LocalizedText] || lt.en;

  return (
    <section id="articles" className="py-14 md:py-20 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            {t('home.articles.title')}
          </h2>
          <p className="mt-3 text-foreground-600">{t('home.articles.subtitle')}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link
            to={`/${lang}/spreadsheet-guide`}
            className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
          >
            <div className="w-full h-48 overflow-hidden">
              <img
                src={IMAGES.article}
                alt="USFans Spreadsheet complete guide"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5 md:p-6 flex flex-col flex-1">
              <span className="inline-flex self-start rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold px-3 py-1">
                {t('home.articles.tag')}
              </span>
              <h3 className="mt-3 font-heading font-semibold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                {t('home.articles.cardTitle')}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed flex-1">
                {t('home.articles.cardDesc')}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                {t('home.articles.readMore')}
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>

          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/${lang}/${p.slug}`}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={ARTICLE_IMAGES[p.slug]}
                  alt={pick(p.title)}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5 md:p-6 flex flex-col flex-1">
                <span className="inline-flex self-start rounded-full bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1">
                  {p.tag}
                </span>
                <h3 className="mt-3 font-heading font-semibold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {pick(p.title)}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed flex-1">
                  {pick(p.description)}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                  {t('home.articles.readMore')}
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}